import { CreateTourThemeModal } from '@carboncopyco/components';
import type { eventWithTime } from '@carboncopyco/rrweb-types';
import type {
  FrameType,
  MediaContent,
  Tour,
  TourAspectRatio,
  TourFrame,
  TourStep,
  TourStepConfigUpdateRequest,
  TourStepTooltipConfig,
  onAddStepProps,
} from '@carboncopyco/types';
import {
  type TourStepAddDirection,
  TourStepEngineEditorMessageType,
} from '@carboncopyco/types';
import { ColorHex, LoadingDots } from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  Debug,
  useAnalytics,
} from '@carboncopyco/utils';
import { scopedLogger, Scope } from '@carboncopyco/logger';
import {
  type ForwardedRef,
  type RefObject,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useStepPosition } from '../../hooks/useStepPosition';

import { useTourDispatch, useTourSelector } from '../../hooks';
import {
  useFrameChunks,
  usePlayableFrames,
  usePlayableSteps,
  useReplayerActions,
  useReplayerActiveState,
  useReplayerListeners,
  useReplayerReady,
} from '../../hooks/replayer';
import {
  selectAddingStepInProgress,
  selectAspectRatio,
  selectIsScreenshotAsFrameUploading,
  setActiveStepId,
  setAspectRatio,
} from '../../store';

import { getStepConfig } from '../../utils/tooltip';

import { TourPlayer, type TourPlayerRef } from '../../components/TourPlayer';
import { useIsAnyFrameScreenshot } from '../../hooks/useIsAnyFrameScreenshot';
import { sendNavigationMessageToParent } from '../../utils/tourStitching';
import { useTourEditorPageProps } from './TourEditorPageContext';
import useFrameWarnings from './components/FrameList/Frame/useFrameWarnings';
import useTooltipMedia from './useTooltipMedia';

const debug = Debug.extend('ToursReplayerEditor');
const engineLogger = scopedLogger(Scope.TOUR_ENGINE);

interface ToursReplayerEditorProps {
  events: eventWithTime[];
  frames: TourFrame[];
  draftSettings: Tour['draftSettings'];
  onEditStep: ({
    step,
    frameId,
    onSuccess,
  }: {
    step: TourStep;
    frameId?: TourFrame['id'];
    onSuccess?: () => void;
  }) => void;
  onAddStep: ({ step, referenceStepId, direction }: onAddStepProps) => void;
  onUpdateAllSteps: (onSuccess?: () => void) => void;
  fileServiceApiToken: string;
  isUpdatingAllSteps: boolean;
  onStepConfigUpdate: (updateRequest: TourStepConfigUpdateRequest) => void;
  onStepChange: (stepId: string) => void;
  hasStepConfigUpdates?: RefObject<boolean>;
  selectFrameByIndex?: (frameIndex: number) => void;
  tabId?: number;
  activeTabId?: number;
  rootElement?: RefObject<HTMLDivElement>;
  organizationId: string;
  tourId: string;
  workspaceId: string;
}

export interface ReplayerEditorRef {
  playFrame: (frame: TourFrame) => void;
  playStep: (stepId: TourStep['stepId']) => void;
  playFrameByStepId: (stepId: TourStep['stepId']) => void;
  deleteStep: () => void;
  addStep: (
    frame: TourFrame,
    direction?: TourStepAddDirection,
    stepId?: TourStep['stepId']
  ) => void;
  cancelInspectMode: () => void;
  refreshTooltip: (step?: TourStep) => void;
  removeHighlight: () => void;
}

interface TourStepEngineEditorAction {
  type: TourStepEngineEditorMessageType;
  payload?: {
    step?: TourStep;
    stepId?: TourStep['stepId'];
    xpath?: TourStep['xpath'];
    containingFrameId?: TourFrame['id'];
    stepConfigUpdateRequest?: TourStepConfigUpdateRequest;
    addStepOptions?: {
      referenceStepId: TourStep['stepId'];
      direction: TourStepAddDirection;
    };
    frameId?: TourFrame['id'];
    elementPosition?: TourStep['elementPosition'];
    frameType?: FrameType;
    media?: string;
    eventName?: string;
    type?: MediaContent['type'];
  };
}

export const ToursReplayerEditor = forwardRef<
  ReplayerEditorRef,
  ToursReplayerEditorProps
>(
  (
    {
      events,
      frames,
      onAddStep,
      onEditStep,
      fileServiceApiToken,
      onUpdateAllSteps,
      isUpdatingAllSteps,
      onStepConfigUpdate,
      onStepChange,
      hasStepConfigUpdates,
      selectFrameByIndex,
      tabId,
      activeTabId,
      rootElement,
      draftSettings,
      organizationId,
      tourId,
      workspaceId,
    }: ToursReplayerEditorProps,
    ref: ForwardedRef<ReplayerEditorRef>
  ) => {
    const dispatch = useTourDispatch();
    const { trackEvent } = useAnalytics();

    const { uuid, mimesisStepEngineUrl, fileServiceDynamicLink, isDebugMode } =
      useTourEditorPageProps();
    const playableFrames = usePlayableFrames(uuid, frames, false);
    const playableSteps = usePlayableSteps(playableFrames);
    const { activeFrame, activeStep } = useReplayerActiveState(frames, uuid);

    const { framesWarnings, removeWarning, calculateAndStoreStepWarning } =
      useFrameWarnings();

    const { isReady } = useReplayerReady(playableFrames, events, uuid);
    const { frameChunks, getCurrentFrameChunk } = useFrameChunks(
      frames,
      events,
      uuid,
      fileServiceApiToken
    );

    const playerRef = useRef<TourPlayerRef>(null);

    const addingStepInProgress = useTourSelector(
      selectAddingStepInProgress(uuid)
    );
    const aspectRatioFromStore = useTourSelector(selectAspectRatio(uuid));

    const isAnyFrameScreenshot = useIsAnyFrameScreenshot(frames);

    const { uploadTooltipMedia } = useTooltipMedia({
      fileServiceApiToken,
      organizationId,
      onUploadProgress: (
        uploadProgress: number,
        fileServiceUrl: string,
        type: MediaContent['type']
      ) => {
        playerRef.current?.onTooltipMediaUpload(
          uploadProgress,
          fileServiceUrl,
          type
        );
      },
    });

    const {
      lastPlayedStep,
      playFrame,
      playFrameByStepId,
      playStep,
      showTooltip,
      incrementalStepNavigation,
      onPlayHandler,
      onPauseHandler,
      onTooltipDisplayed,
      refreshTooltip,
    } = useReplayerActions({
      isReady,
      playerRef,
      frameChunks,
      playableFrames,
      playableSteps,
      activeFrame,
      activeStep,
      settings: draftSettings,
      rootElement,
      addingStepInProgress,
      onStepChange: ({ stepId }) => onStepChange(stepId),
      uuid,
      framesWarnings,
    });

    const onPostMessage = (event: {
      data: TourStepEngineEditorAction;
    }) => {
      const { type, payload } = event.data;
      methods[type]?.({
        step: payload?.step,
        containingFrameId: payload?.containingFrameId,
        addStepOptions: payload?.addStepOptions,
        xpath: payload?.xpath,
        elementPosition: payload?.elementPosition,
        stepConfigUpdateRequest: payload?.stepConfigUpdateRequest,
        frameId: payload?.frameId,
        frameType: payload?.frameType,
        stepId: payload?.stepId,
        media: payload?.media,
        eventName: payload?.eventName,
        type: payload?.type,
      });
      if (type !== TourStepEngineEditorMessageType.onAnalyticsEvent) {
        engineLogger.info(`Tour editor event: ${type}`, payload);
      }
    };

    useReplayerListeners({
      playableFrames,
      playableSteps,
      activeFrame,
      activeStep,
      lastPlayedStep,
      activeTabId,
      tabId,
      onPostMessageHandler: onPostMessage,
      refreshTooltip,
      incrementalStepNavigation,
    });

    const [tourAspectRatio, setTourAspectRatio] = useState<TourAspectRatio>(
      draftSettings.responsiveness ? 'responsive' : 'fixedWidthAndHeight'
    );

    useEffect(() => {
      if (aspectRatioFromStore) {
        setTourAspectRatio(aspectRatioFromStore);
        dispatch(setAspectRatio({ uuid: uuid, aspectRatio: null }));
      }
    }, [aspectRatioFromStore]);

    useEffect(() => {
      setTourAspectRatio(
        draftSettings.responsiveness ? 'responsive' : 'fixedWidthAndHeight'
      );
    }, [draftSettings.responsiveness]);

    const isScreenshotAsFrameUploading = useTourSelector(
      selectIsScreenshotAsFrameUploading(uuid)
    );

    const onUpdateAllStepsRequestHandler = () => {
      onUpdateAllSteps(() => {
        playerRef.current?.setHasStepConfigUpdates(
          !!hasStepConfigUpdates?.current
        );
      });
    };

    const onEditStepRequestHandler = ({
      step,
      containingFrameId,
    }: {
      step?: TourStep;
      containingFrameId?: TourFrame['id'];
    }) => {
      if (!step) {
        return;
      }

      if (activeStep?.showTooltip !== step.showTooltip) {
        calculateAndStoreStepWarning(activeFrame.id, step, step.showTooltip);

        trackEvent(AnalyticsTrackEventName.TOUR_TOGGLE_SHOW_SPECIFIC_TOOLTIP, {
          location: 'tour_editor_settings',
          checkedItem: step.showTooltip,
          stepId: step.stepId,
          timestamp: Date.now(),
        }).catch((error) => {
          debug('Failed to send analytics event', error);
        });
      }

      onEditStep({
        step,
        frameId: containingFrameId,
        onSuccess: () => {
          playerRef.current?.setHasStepConfigUpdates(
            !!hasStepConfigUpdates?.current
          );
        },
      });
    };

    const elementSelectedRequestHandler = ({
      step,
      addStepOptions,
      frameType,
    }: {
      step?: TourStep;
      addStepOptions: {
        referenceStepId: TourStep['stepId'];
        direction: TourStepAddDirection;
      };
      frameType?: FrameType;
    }) => {
      if (!step) {
        return;
      }
      const stepConfig = getStepConfig({
        step,
        activeFrame,
        playableFrames,
        addStepDirection: addStepOptions?.direction,
        addStepReferenceStepId: addStepOptions?.referenceStepId,
        shouldInheritConfig: true,
        frameType,
        rootElement,
      });

      const stepWithConfig = {
        ...step,
        config: stepConfig,
        showTooltip: draftSettings.showTooltips,
      };
      onAddStep({
        step: stepWithConfig,
        referenceStepId: addStepOptions?.referenceStepId,
        direction: addStepOptions?.direction,
      });
      dispatch(
        setActiveStepId({
          stepId: stepWithConfig.stepId,
          uuid,
        })
      );
    };

    const switchElementSelectedRequestHandler = ({
      step,
      xpath,
      elementPosition,
      frameType,
    }: {
      step?: TourStep;
      xpath?: TourStep['xpath'];
      elementPosition?: TourStep['elementPosition'];
      frameType?: FrameType;
    }) => {
      debug('switchElementSelectedRequestHandler', {
        step,
        xpath,
        elementPosition,
      });

      if (!step || (!xpath && !elementPosition)) {
        return;
      }

      const newStep: TourStep = {
        ...step,
        xpath,
        elementPosition,
        config: getStepConfig({
          step: { ...step, xpath, elementPosition },
          activeFrame,
          playableFrames,
          shouldInheritConfig: false,
          frameType,
          rootElement,
        }),
      };

      onEditStep({
        step: newStep,
        frameId: activeFrame.id,
      });
      showTooltip({
        step: newStep,
        isEditable: true,
        frameId: activeFrame.id,
      });

      removeWarning(activeFrame.id, step.stepId, 'position');

      trackEvent(
        AnalyticsTrackEventName.TOUR_CLICK_TOOLTIP_RESELECT_TARGET_ELEMENT,
        {
          location: 'tour_editor_tooltip',
          stepId: step.stepId,
        }
      );
    };

    const onStepConfigUpdateRequestHandler = (
      payload: TourStepConfigUpdateRequest
    ) => {
      onStepConfigUpdate(payload);
    };

    const onRestartRequestHandler = () => {
      playStep(playableSteps[0].stepId);
    };

    const onDismissPositionWarningHandler = ({
      stepId,
      frameId,
    }: {
      stepId: TourStep['stepId'];
      frameId: TourFrame['id'];
    }) => {
      removeWarning(frameId, stepId, 'position');
    };

    const onNextStepRequestHandler = (payload: {
      keyboardNavigation?: boolean;
    }) => {
      sendNavigationMessageToParent('next');
      incrementalStepNavigation('next', !!payload?.keyboardNavigation);
    };

    const onBackStepRequestHandler = (payload: {
      keyboardNavigation?: boolean;
    }) => {
      sendNavigationMessageToParent('back');
      incrementalStepNavigation('back', !!payload?.keyboardNavigation);
    };

    const onMediaUploadHandler = ({
      media,
      type,
    }: {
      media: string;
      type: MediaContent['type'];
    }) => {
      uploadTooltipMedia(media, type);
    };

    const [isTourThemeCreateModalOpen, setIsTourThemeCreateModalOpen] =
      useState(false);
    const [tourThemeTooltipConfig, setTourThemeTooltipConfig] =
      useState<TourStepTooltipConfig>();
    const onOpenTourThemeCreateModal = ({ step }: { step: TourStep }) => {
      setTourThemeTooltipConfig(step.config.tooltip);
      setIsTourThemeCreateModalOpen(true);
    };

    const onAnalyticsEventHandler = (payload: {
      eventName: string;
    }) => {
      trackEvent(payload.eventName, {
        tourId,
      });
    };

    const methods: Record<
      TourStepEngineEditorMessageType,
      (payload: any) => void
    > = {
      [TourStepEngineEditorMessageType.elementSelected]:
        elementSelectedRequestHandler,
      [TourStepEngineEditorMessageType.onEditStep]: onEditStepRequestHandler,
      [TourStepEngineEditorMessageType.onStepEngineReady]: () =>
        refreshTooltip(),
      [TourStepEngineEditorMessageType.updateAllSteps]:
        onUpdateAllStepsRequestHandler,
      [TourStepEngineEditorMessageType.switchElementSelected]:
        switchElementSelectedRequestHandler,
      [TourStepEngineEditorMessageType.onStepConfigUpdate]: ({
        stepConfigUpdateRequest,
      }) =>
        stepConfigUpdateRequest &&
        onStepConfigUpdateRequestHandler(stepConfigUpdateRequest),
      [TourStepEngineEditorMessageType.onTooltipDisplayed]: onTooltipDisplayed,
      [TourStepEngineEditorMessageType.onNextStep]: onNextStepRequestHandler,
      [TourStepEngineEditorMessageType.onBackStep]: onBackStepRequestHandler,
      [TourStepEngineEditorMessageType.onRestartTour]: onRestartRequestHandler,
      [TourStepEngineEditorMessageType.onDismissPositionWarning]:
        onDismissPositionWarningHandler,
      [TourStepEngineEditorMessageType.onMediaUpload]: onMediaUploadHandler,
      [TourStepEngineEditorMessageType.onOpenTourThemeCreateModal]:
        onOpenTourThemeCreateModal,
      [TourStepEngineEditorMessageType.onAnalyticsEvent]:
        onAnalyticsEventHandler,
    };

    const deleteStep = () => {
      playerRef.current?.removeStep();
    };

    const addStep = (
      frame: TourFrame,
      direction?: TourStepAddDirection,
      stepId?: TourStep['stepId']
    ) => {
      playerRef?.current?.setInspectMode({
        frameType: frame.frameType,
        addStepOptions: {
          referenceStepId: stepId,
          direction,
        },
      });
    };

    const cancelInspectMode = () => {
      playerRef?.current?.cancelInspectMode();
    };

    const removeHighlight = () => {
      playerRef?.current?.removeHighlight();
    };

    useEffect(() => {
      playerRef.current?.setIsApplyingConfigChanges(isUpdatingAllSteps);
    }, [isUpdatingAllSteps]);

    useEffect(() => {
      playerRef.current?.setHasStepConfigUpdates(
        !!hasStepConfigUpdates?.current
      );
    }, [hasStepConfigUpdates]);

    useImperativeHandle(
      ref,
      () => ({
        playFrame,
        playFrameByStepId,
        playStep,
        deleteStep,
        addStep,
        cancelInspectMode,
        refreshTooltip,
        removeHighlight,
      }),
      [
        events,
        playableFrames,
        activeFrame,
        activeStep,
        draftSettings,
        framesWarnings,
      ]
    );

    const { isTourFirstStep, isTourLastStep } = useStepPosition({
      playableSteps,
      activeStep,
    });

    return isReady ? (
      <>
        <TourPlayer
          ref={playerRef}
          events={events}
          fileServiceApiToken={fileServiceApiToken}
          isBuffering={false}
          onPauseHandler={onPauseHandler}
          onPlayHandler={onPlayHandler}
          incrementalStepNavigation={incrementalStepNavigation}
          onRestartHandler={onRestartRequestHandler}
          editorMode
          selectFrameByIndex={selectFrameByIndex}
          mimesisStepEngineUrl={mimesisStepEngineUrl}
          fileServiceDynamicLink={fileServiceDynamicLink}
          isDebugMode={isDebugMode}
          activeFrameId={activeFrame?.id}
          activeStep={activeStep}
          playableFrames={playableFrames}
          isAnyFrameScreenshot={isAnyFrameScreenshot}
          isLoading={isScreenshotAsFrameUploading}
          tourAspectRatio={tourAspectRatio}
          tourSettings={draftSettings}
          frameChunk={getCurrentFrameChunk(activeFrame)}
          activeTabId={activeTabId}
          isTourLastStep={isTourLastStep}
          isTourFirstStep={isTourFirstStep}
          rootElement={rootElement}
        />
        <CreateTourThemeModal
          workspaceId={workspaceId}
          open={isTourThemeCreateModalOpen}
          onClose={() => setIsTourThemeCreateModalOpen(false)}
          organizationId={organizationId}
          tourId={tourId}
          tooltipConfig={tourThemeTooltipConfig}
          isTourLastStep={isTourLastStep}
        />
      </>
    ) : (
      <div className="flex w-full h-full justify-center items-center">
        <LoadingDots color={ColorHex.natural600} />
      </div>
    );
  }
);

ToursReplayerEditor.displayName = 'ToursReplayerEditor';
